/**
 * Micro-Infusion Hair Treatment Landing Page Configuration
 * 
 * This is the default landing page (/) targeting the Colagea Micro-Infuse® treatment.
 * Adapted for the US market from colagea.com.
 */
import type { PartialLandingPageConfig } from './types';
import { createConfig } from './utils';

const downAlternativePartial: PartialLandingPageConfig = {
  id: 'down-alternative',
  name: 'Micro-Infusion Hair Treatment',
  slug: 'down-alternative',
  pageType: 'angle' as const,
  category: 'legacy',
  
  seo: {
    title: 'Colagea | Micro-Infuse® Hair Treatment - Clinically Proven Hair Regrowth',
    description: 'Stop hair loss at the root. Colagea Micro-Infuse® activates dormant follicles to visibly densify hair in just weeks. Dermatologically tested. 60-day money-back guarantee.',
    keywords: ['hair regrowth treatment', 'micro infusion hair', 'hair loss treatment men', 'scalp treatment', 'hair densifying', 'follicle activation'],
  },
  
  hero: {
    headline: 'Clinically Proven Hair Regrowth. Visible in 5 Weeks.',
    subheadline: 'Colagea Micro-Infuse® activates dormant follicles at the root to stop thinning and visibly densify your hair. Dermatologically tested, backed by science.',
    trustBadge: {
      reviewCount: '4,600+',
      rating: '4.6 Star',
      source: 'Reviews',
    },
    usps: [
      { icon: 'support', title: 'Activates Follicles', description: 'Targets hair loss at the root' },
      { icon: 'temperature', title: 'Visible in 5 Weeks', description: 'Clinically proven results' },
      { icon: 'shield', title: 'Dermatologically Tested', description: 'Safe for daily use' },
      { icon: 'clock', title: 'Backed by Science', description: 'Peer-reviewed clinical studies' },
    ],
    showOekoTexBadge: false,
    showOprahBadge: false,
    showDermaBadge: true,
    show30NightsBadge: true,
  },
  
  benefits: {
    sectionTitle: 'End Your Hair Loss Battle',
    sectionSubtitle: 'Thinning temples, receding hairline, or sparse crown? Micro-Infuse® technology targets the root cause and delivers visible density where you need it most.',
    benefits: [
      { icon: 'moon', title: 'Stop Thinning at the Source', description: 'Frustrated with products that don\'t work? Colagea Micro-Infuse® penetrates deep to reactivate dormant follicles, clinically proven to reduce hair loss.' },
      { icon: 'sparkles', title: 'Visibly Thicker Hair', description: 'See real density return in as little as 4 weeks. No transplants, no prescriptions, just science-backed micro-infusion technology.' },
      { icon: 'refresh', title: 'Simple Daily Routine', description: 'Takes just 2 minutes. Apply, massage, go. No mess, no downtime. Fits seamlessly into your morning or evening routine.' },
    ],
  },
  
  difference: {
    standardPillowTitle: 'Generic Hair Products Fall Short',
    standardPillowPoints: [
      'Surface-level treatments that wash away',
      'Harsh chemicals that irritate the scalp',
      'Months of use with no visible results',
      'Expensive prescriptions with side effects',
      'One-size-fits-all formulas',
    ],
    fluffcoPillowTitle: 'Colagea Micro-Infuse® Delivers Results',
    fluffcoPillowPoints: [
      'Micro-infusion reaches follicles at the root',
      'Gentle, dermatologically tested formula',
      'Visible density improvement in weeks',
      'No prescription needed, no side effects',
      'Targeted treatment for your specific pattern',
    ],
  },
  
  testimonials: {
    sectionTitle: '+4,600 Verified Reviews',
    sectionSubtitle: 'Real Results from Real Men',
    testimonials: [
      { name: 'David R.', location: 'Austin, TX', verified: true, content: 'I was skeptical at first, but after 6 weeks my barber actually asked what I was doing differently. The thinning at my temples is noticeably filling in. Genuinely impressed.' },
      { name: 'Marcus T.', location: 'Seattle, WA', verified: true, content: 'Tried everything: minoxidil, supplements, expensive shampoos. This is the first product where I can actually see new growth coming in. My crown area looks so much better.' },
      { name: 'Ryan K.', location: 'Chicago, IL', verified: true, content: 'The application is so easy, literally takes 2 minutes. After the second month, my hair feels thicker and I\'m not finding clumps in the shower anymore. Game changer.' },
      { name: 'Chris P.', location: 'Portland, OR', verified: true, content: 'My wife noticed the difference before I did. She said my hair looks fuller and healthier. I\'m on month 3 now and I\'m not stopping. Best investment I\'ve made for myself.' },
      { name: 'James L.', location: 'Boston, MA', verified: true, content: 'I started losing hair at 28 and it destroyed my confidence. After 2 months with Colagea, I can see baby hairs growing in areas that were completely bare. I feel like myself again.' },
      { name: 'Anthony M.', location: 'Denver, CO', verified: true, content: 'The 60-day guarantee gave me the confidence to try it. I didn\'t need it though — results were visible by week 5. My hairline is actually coming back. Unreal.' },
      { name: 'Michael S.', location: 'Phoenix, AZ', verified: true, content: 'I work in finance and appearance matters. This treatment has given me back the thick hair I had in my 20s. Colleagues have even commented on it. Worth every penny.' },
      { name: 'Kevin W.', location: 'Miami, FL', verified: true, content: 'No side effects, no greasy residue, no weird smell. Just apply and forget about it. 8 weeks in and my hair is visibly denser. Finally something that actually works.' },
    ],
    showMoreButton: true,
  },
  
  // Colagea Science Section
  fluffcoSecret: {
    features: [
      { title: 'Micro-Infusion Technology', description: 'Our patented Micro-Infuse® delivery system uses nano-sized active molecules that penetrate the scalp barrier to reach follicles directly at the root, where hair growth begins.', bulletPoints: ['Reaches Dormant Follicles', 'Nano-Sized Molecules', 'Deep Scalp Penetration', 'Activates Growth Phase', 'Clinically Validated'] },
      { title: 'Bioactive Peptide Complex', description: 'Each treatment contains a concentrated blend of bioactive peptides and growth factors that signal follicles to re-enter the active growth phase, increasing hair density and thickness.', bulletPoints: ['Stimulates New Growth', 'Strengthens Existing Hair', 'Reduces Shedding', 'Improves Scalp Health', 'Dermatologically Tested'] },
    ],
  },
  
  // Expert Section
  sleepExperts: {
    sectionTitle: 'The Treatment Dermatologists Recommend',
    sectionSubtitle: '',
    cards: [
      { image: '/images/expert-comfort.png', title: 'Clinical-Grade Formula, No Prescription', expandedContent: ['Developed with leading dermatologists. Colagea Micro-Infuse® delivers clinical results without the side effects of prescription treatments.', 'Dermatologically Tested & Approved'] },
      { image: '/images/expert-cool.png', title: 'Results You Can See and Feel', expandedContent: ['Visible hair density improvement in as little as 4 weeks. 93% of users report reduced hair loss after 60 days.', 'Backed by Independent Clinical Studies'] },
    ],
  },
  
  // FAQ Section
  faq: {
    sectionTitle: 'Common Questions',
    items: [
      { question: 'How does Colagea Micro-Infuse® work?', answer: 'Our patented Micro-Infuse® technology uses nano-sized active molecules that penetrate the scalp to reach hair follicles directly at the root. This reactivates dormant follicles and stimulates them to re-enter the active growth phase, resulting in visibly thicker, denser hair.' },
      { question: 'When will I see results?', answer: 'Most users notice reduced hair shedding within 2-3 weeks. Visible new growth and increased density typically appear between weeks 4-8. For optimal results, we recommend the 4-month treatment course.' },
      { question: 'Is it safe? Are there side effects?', answer: 'Colagea Micro-Infuse® is dermatologically tested and formulated without harsh chemicals. Unlike prescription treatments, our formula has no known side effects. It\'s gentle enough for daily use on all skin types.' },
      { question: 'How do I use it?', answer: 'Apply the micro-infusion treatment directly to the scalp in targeted areas. Massage gently for 30 seconds. Use once daily, preferably in the evening. Each application takes less than 2 minutes. No rinsing required.' },
      { question: 'Which treatment plan should I choose?', answer: 'The 1-month cure (3 treatments) is great for trying the product. The 2-month cure (6 treatments) is our most popular option and recommended for noticeable results. The 4-month cure (12 treatments) delivers optimal, lasting results and the best value.' },
      { question: 'What is your satisfaction guarantee?', answer: 'We offer a 60-day satisfaction guarantee. If you\'re not seeing results within 60 days, contact us for a full refund. We\'re confident in our product because the science backs it up.' },
      { question: 'How long until I receive my order?', answer: 'Orders typically ship within 1-2 business days. Standard delivery takes 3-5 business days within the continental US. Free shipping is included on all orders.' },
    ],
  },
};

export const downAlternativeConfig = createConfig(downAlternativePartial);
export default downAlternativeConfig;
